import { ChangeDetectionStrategy, Component, Inject, OnInit } from '@angular/core'
import { PageEvent } from '@angular/material/paginator'
import { ColumnTypeEnum, GbTableAction, GbTableColumn, GridSwitchEnum } from '@galabid/gb-dashboard/shared/feature'
import {
  SITE_ANNOUNCEMENTS_LOADER,
  SiteAnnouncementsEntity,
  SiteAnnouncementsFacade,
  siteAnnouncementsEntityName,
} from '@galabid/gb-dashboard/staff/data-access-site-announcements'
import { EnvironmentModel } from 'apps/gb-dashboard/src/environments/environment.model'
import { IconLibrary } from 'libs/gb-dashboard/shared/ui/src/lib/gb-icon/gb-icon-library.models'
import * as SiteAnnouncementsActions from 'libs/gb-dashboard/staff/data-access-site-announcements/src/lib/+state/site-announcements.actions'
import { APP_CONFIG } from 'libs/shared/app-config/src/lib/gb-app-config'
import { StringUtils } from 'libs/shared/utils/helpers/src/lib/string-utils'
import { Observable, combineLatest, map, of } from 'rxjs'

interface SiteAnnouncementsWithActions extends SiteAnnouncementsEntity {
  display_label: string
  target_label: string
  actions: GbTableAction[]
}

@Component({
  selector: 'gb-staff-announcements',
  templateUrl: './gb-staff-announcements.component.html',
  styleUrls: ['./gb-staff-announcements.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class GbStaffAnnouncementsComponent implements OnInit {
  siteAnnouncementsEntityName = siteAnnouncementsEntityName
  loaded$ = this.siteAnnouncementsFacade.loaded$
  allSiteAnnouncements$ = this.siteAnnouncementsFacade.allSiteAnnouncements$
  allSiteAnnouncementsWithActions$ = this.allSiteAnnouncements$.pipe(
    map((allSiteAnnouncements): SiteAnnouncementsWithActions[] =>
      allSiteAnnouncements
        .map((siteAnnouncement) => ({
          ...siteAnnouncement,
          display_label: siteAnnouncement.display ? 'Yes' : 'No',
          target_label: StringUtils.toProperCase(siteAnnouncement.target),
          actions: [
            {
              name: 'EDIT',
              icon: 'edit',
              iconLibrary: IconLibrary.material,
              method: () =>
                this.siteAnnouncementsFacade.dispatch(
                  SiteAnnouncementsActions.selectSiteAnnouncement({
                    uuid: siteAnnouncement.uuid,
                  }),
                ),
            },
            {
              name: 'DELETE',
              icon: 'trash',
              method: () =>
                this.siteAnnouncementsFacade.dispatch(
                  SiteAnnouncementsActions.deleteSiteAnnouncement({
                    uuid: siteAnnouncement.uuid,
                    title: siteAnnouncement.title,
                  }),
                ),
            },
          ],
        }))
        .sort((a, b) => new Date(b.publish_date).getTime() - new Date(a.publish_date).getTime()),
    ),
  )
  // @ts-ignore
  displayedColumns$: Observable<GbTableColumn[]> = of([
    {
      columnKey: 'title',
      columnLabel: 'SITE_ANNOUNCEMENTS.TABLE.TITLE',
      columnType: ColumnTypeEnum.Label,
    },
    {
      columnKey: 'display_label',
      columnLabel: 'SITE_ANNOUNCEMENTS.TABLE.DISPLAY',
      columnType: ColumnTypeEnum.Label,
    },
    {
      columnKey: 'publish_date',
      columnLabel: 'SITE_ANNOUNCEMENTS.TABLE.PUBLISH_DATE',
      columnType: ColumnTypeEnum.Date,
    },
    {
      columnKey: 'display_until',
      columnLabel: 'SITE_ANNOUNCEMENTS.TABLE.DISPLAY_UNTIL',
      columnType: ColumnTypeEnum.Date,
    },
    {
      columnKey: 'target_label',
      columnLabel: 'SITE_ANNOUNCEMENTS.TABLE.TARGET',
      columnType: ColumnTypeEnum.Label,
    },
    {
      columnKey: 'actions',
      columnLabel: 'ACTIONS',
      columnType: ColumnTypeEnum.Actions,
    },
  ])

  dataView$ = of(GridSwitchEnum.Table)

  showEmptyState$ = combineLatest([
    this.loaded$,
    this.siteAnnouncementsFacade.allSiteAnnouncements$.pipe(map((annoucements) => annoucements.length)),
  ]).pipe(map(([loaded, count]) => loaded && count === 0))

  limit = this.appConfig.itemsPageSize
  offset = 0

  constructor(
    private siteAnnouncementsFacade: SiteAnnouncementsFacade,
    @Inject(APP_CONFIG) protected appConfig: EnvironmentModel,
  ) {}

  ngOnInit() {
    this.loadSiteAnnouncements()
  }

  private loadSiteAnnouncements() {
    this.siteAnnouncementsFacade.dispatch(
      SiteAnnouncementsActions.loadSiteAnnouncements({
        limit: this.limit,
        offset: this.offset,
      }),
    )
  }

  onPageChange(event: PageEvent) {
    this.limit = event.pageSize
    this.offset = event.pageIndex * event.pageSize
    this.loadSiteAnnouncements()
  }

  openCreateSiteAnnouncement() {
    this.siteAnnouncementsFacade.dispatch(SiteAnnouncementsActions.selectSiteAnnouncement({ uuid: undefined }))
  }
}
